import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ClipboardCheck, Download, Plus, Search, Table2 } from 'lucide-react'
import {
  useAssignPrograms,
  useContentSummaries,
  useDefaultSegments,
  useMapSlateProgram,
  usePrograms,
  useSegmentMutations,
  useSegments,
  useSlatePrograms,
  useSuggestedPrograms,
  useUpsertProgram,
  useWbProgramMutations,
  useWbPrograms,
} from '@/api/queries'
import { ApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { DataTable } from '@/components/ui/DataTable'
import { Input, Select } from '@/components/ui/Field'
import { EmptyState } from '@/components/ui/States'
import { useToast } from '@/components/ui/Toast'
import { BulkActionBar } from '@/components/review/BulkActionBar'
import { ReviewSurface } from '@/components/review/ReviewSurface'
import { StatusTabs } from '@/components/review/StatusTabs'
import { useReviewCollection } from '@/components/review/useReviewCollection'
import { useReviewQueue } from '@/components/review/useReviewQueue'
import { SegmentRail, UNASSIGNED } from './SegmentRail'
import { ProgramDrawer } from './ProgramDrawer'
import { ProgramReviewQueue } from './ProgramReviewQueue'
import { DeleteSegmentModal } from './DeleteSegmentModal'
import { SegmentDetailsModal } from './SegmentDetailsModal'
import { LEVEL_VIEWS, LevelTabs, ViewTabs, type ProgramLevel, type ProgramView } from './ViewTabs'
import { TranslationsModal } from './TranslationsModal'
import { WbProgramDrawer } from './WbProgramDrawer'
import { SlateContentDrawer } from './SlateContentDrawer'
import { forwardRows, isGraduate, isOffered, reverseRows, translationsCsv } from './translations'
import { ProgramQuickAdd } from './ProgramQuickAdd'
import { unusedSuggestions } from './suggestions'
import {
  buildColumns,
  buildGraduateColumns,
  buildMergedColumns,
  buildOtherColumns,
  buildSlateColumns,
  buildUnassignedColumns,
  levelLabel,
  programLabel,
  refLabel,
  wbProgramOptions,
} from './columns'
import { groupPrograms, memberIds, programGroupKey } from './groups'
import { NOT_STATED, filterOptions, matchesAttribute } from './filters'
import { downloadBlob } from '@/lib/csv'
import { countLabel } from '@/lib/format'
import type { Program, Segment, SlateProgram, WbProgram, WbProgramTarget } from '@/types'

const searchText = (p: Program) =>
  `${p.wbProgram ?? ''} ${p.name} ${p.degree ?? ''} ${p.description ?? ''}`
const slateSearchText = (s: SlateProgram) =>
  `${s.slateName} ${s.wbProgram?.name ?? ''} ${s.mappedPrograms.map(refLabel).join(' ')}`

/** Merged duplicates live on their own tab (migrations/022). */
const isSurvivor = (p: Program) => p.mergedInto === null
/**
 * The Programs tab's crawled rows: undergraduate survivors under a WB program
 * with a page of their own. Unassigned ones have their own tab (PC-33);
 * page-less ones are Other programs; graduate ones are the Graduate tab's.
 */
const isListed = (p: Program) => isSurvivor(p) && !isGraduate(p) && p.wbProgramId !== null && isOffered(p)
/**
 * The Graduate tab's Programs (PC-25): graduate survivors with a page of their
 * own, each its own row. They have no WB program (ADR-0022).
 */
const isGraduateListed = (p: Program) => isSurvivor(p) && isGraduate(p) && isOffered(p)

const mergedColumns = buildMergedColumns()
const otherColumns = buildOtherColumns()

export function ProgramsPage() {
  const { clientId } = useParams()
  const { data: segments = [] } = useSegments(clientId!)
  const { data: defaultSegments = [] } = useDefaultSegments(clientId!)
  const { data: suggestedPrograms = [] } = useSuggestedPrograms(clientId!)
  const { data: allPrograms = [], isLoading: programsLoading } = usePrograms(clientId!)
  const { data: slatePrograms = [], isLoading: slateLoading } = useSlatePrograms(clientId!)
  const { data: wbPrograms = [], isLoading: wbLoading } = useWbPrograms(clientId!)
  const { data: summaries } = useContentSummaries(clientId!)
  const upsert = useUpsertProgram(clientId!)
  const assign = useAssignPrograms(clientId!)
  const mapSlate = useMapSlateProgram(clientId!)
  const wbOps = useWbProgramMutations(clientId!)
  const segmentOps = useSegmentMutations(clientId!)
  const toast = useToast()

  const [level, setLevel] = useState<ProgramLevel>('undergraduate')
  const graduate = level === 'graduate'
  const [view, setView] = useState<ProgramView>('programs')
  const [segmentFilter, setSegmentFilter] = useState('all')
  // The Programs view's degree filters (PC-28): extracted values, 'all' or NOT_STATED.
  const [levelFilter, setLevelFilter] = useState('all')
  const [degreeFilter, setDegreeFilter] = useState('all')
  const attributeFiltered = levelFilter !== 'all' || degreeFilter !== 'all'
  const [editingWb, setEditingWb] = useState<WbProgram | 'new' | null>(null)
  // The name a quick add (PC-31) fills in while `editingWb` is 'new'.
  const [quickAddName, setQuickAddName] = useState<string | undefined>()
  const [editing, setEditing] = useState<Program | 'new' | null>(null)
  // The WB program a new degree goes under, while `editing` is 'new'.
  const [addTo, setAddTo] = useState<WbProgram | null>(null)
  const [deletingSegment, setDeletingSegment] = useState<Segment | null>(null)
  // By id, so the modal reads the segment as the list refetches it.
  const [detailsSegmentId, setDetailsSegmentId] = useState<string | null>(null)
  const [viewingTranslations, setViewingTranslations] = useState(false)
  // By id, so the drawer reads the slate program as the list refetches it.
  const [slateContentId, setSlateContentId] = useState<string | null>(null)
  // The selected rows of the Programs tab, by WB program; review actions use their crawled rows.
  const [selectedWb, setSelectedWb] = useState<Set<string>>(new Set())

  const wbById = useMemo(() => new Map(wbPrograms.map((w) => [w.id, w])), [wbPrograms])
  const wbOptions = useMemo(() => wbProgramOptions(wbPrograms), [wbPrograms])
  const wbContent = useMemo(() => new Map((summaries?.wbPrograms ?? []).map((c) => [c.id, c])), [summaries])
  const slateContent = useMemo(() => new Map((summaries?.slatePrograms ?? []).map((c) => [c.id, c])), [summaries])
  // Read the open drawer's WB program from the list, so it shows the latest after a write.
  const openWb = editingWb === 'new' || editingWb === null ? editingWb : (wbById.get(editingWb.id) ?? null)

  const segmentMatches = useCallback(
    (segmentId: string | null) => {
      if (segmentFilter === 'all') return true
      if (segmentFilter === UNASSIGNED) return segmentId === null
      return segmentId === segmentFilter
    },
    [segmentFilter],
  )
  // A crawled row's segment is its WB program's.
  const bySegment = useCallback(
    (p: Program) => segmentMatches(wbById.get(p.wbProgramId ?? '')?.segmentId ?? null),
    [segmentMatches, wbById],
  )
  // A WB program's row then lists only the degrees that match.
  const byAttributes = useCallback(
    (p: Program) => matchesAttribute(p.level, levelFilter) && matchesAttribute(p.degree, degreeFilter),
    [levelFilter, degreeFilter],
  )
  const undergraduateFilter = useCallback((p: Program) => bySegment(p) && byAttributes(p), [bySegment, byAttributes])

  // Graduate programs have no WB program, so no segment and no grouping (PC-25).
  const collection = useReviewCollection<Program>(clientId!, 'programs', {
    searchText,
    noun: 'program',
    scope: graduate ? isGraduateListed : isListed,
    extraFilter: graduate ? byAttributes : undergraduateFilter,
    groupBy: graduate ? undefined : programGroupKey,
  })
  const levelOptions = useMemo(() => filterOptions(collection.all, 'level'), [collection.all])
  const degreeOptions = useMemo(() => filterOptions(collection.all, 'degree'), [collection.all])

  /**
   * WB programs with no crawled row on this tab: added by hand (Pre-Med), or
   * only page-less degrees. They have no review status, so they are listed
   * with the approved: someone chose to add them.
   */
  const listedWb = useMemo(() => new Set(collection.all.map((p) => p.wbProgramId)), [collection.all])
  const handAdded = useMemo(
    () => (graduate ? [] : wbPrograms.filter((w) => !listedWb.has(w.id))),
    [graduate, wbPrograms, listedWb],
  )
  const needle = collection.query.trim().toLowerCase()
  // They have no degree, so a level or degree filter hides them.
  const keepEmpty = useCallback(
    (w: WbProgram) =>
      !graduate &&
      !attributeFiltered &&
      collection.status === 'approved' &&
      !listedWb.has(w.id) &&
      segmentMatches(w.segmentId) &&
      (!needle || w.name.toLowerCase().includes(needle)),
    [graduate, attributeFiltered, collection.status, listedWb, segmentMatches, needle],
  )
  const counts = useMemo(
    () => ({ ...collection.counts, approved: collection.counts.approved + handAdded.length }),
    [collection.counts, handAdded.length],
  )

  /** One row per WB program, its degrees combined (groups.ts). */
  const groups = useMemo(
    () => groupPrograms(wbPrograms, collection.rows, keepEmpty),
    [wbPrograms, collection.rows, keepEmpty],
  )
  const selectedGroups = useMemo(
    () =>
      new Set(
        groups
          .filter((g) =>
            g.members.length > 0 ? g.members.every((m) => collection.selected.has(m.id)) : selectedWb.has(g.id),
          )
          .map((g) => g.id),
      ),
    [groups, collection.selected, selectedWb],
  )
  const { setSelected, clearSelection } = collection
  const selectGroups = useCallback(
    (keys: Set<string>) => {
      setSelectedWb(keys)
      setSelected(new Set(groups.filter((g) => keys.has(g.id)).flatMap(memberIds)))
    },
    [groups, setSelected],
  )
  const clearAll = useCallback(() => {
    setSelectedWb(new Set())
    clearSelection()
  }, [clearSelection])
  const programCount = graduate ? collection.all.length : listedWb.size + handAdded.length

  /** Every pending degree, a WB program's degrees together (APP-140). */
  const queueOrder = useMemo(
    () => (graduate ? collection.all : groupPrograms(wbPrograms, collection.all).flatMap((g) => g.members)),
    [graduate, wbPrograms, collection.all],
  )
  const queue = useReviewQueue(queueOrder)

  const setWbSegment = (ids: string[], segmentId: string | null, onSuccess?: () => void) =>
    wbOps.setSegment.mutate(
      { ids, segmentId },
      { onSuccess, onError: () => toast.push('Could not change the segment.', 'error') },
    )

  const columns = buildColumns({
    segments,
    onSegmentChange: (wbProgramId, segmentId) => setWbSegment([wbProgramId], segmentId),
    onFlag: (ids, flagged) => ids.forEach((id) => collection.setFlagFor(id, flagged)),
    contentOf: (id) => wbContent.get(id),
  })

  function assignTo(ids: string[], target: WbProgramTarget, label: string) {
    assign.mutate(
      { ids, target },
      {
        onSuccess: () =>
          toast.push(
            target === null
              ? `Unassigned ${label}.`
              : `Moved ${label} to ${'name' in target ? target.name : (wbById.get(target.wbProgramId)?.name ?? 'the program')}.`,
          ),
        onError: () => toast.push(`Could not move ${label}.`, 'error'),
      },
    )
  }

  // The other views share the search box; status and segment do not apply.
  const matchesSearch = useCallback((p: Program) => !needle || searchText(p).toLowerCase().includes(needle), [needle])
  const inLevel = useCallback((p: Program) => isGraduate(p) === graduate, [graduate])
  // Undergraduate only: a graduate program is never assigned (ADR-0022).
  const unassigned = useMemo(
    () => allPrograms.filter((p) => isSurvivor(p) && !isGraduate(p) && p.wbProgramId === null),
    [allPrograms],
  )
  const unassignedRows = useMemo(() => unassigned.filter(matchesSearch), [unassigned, matchesSearch])
  const merged = useMemo(() => allPrograms.filter((p) => !isSurvivor(p) && inLevel(p)), [allPrograms, inLevel])
  const mergedRows = useMemo(() => merged.filter(matchesSearch), [merged, matchesSearch])
  const other = useMemo(
    () =>
      allPrograms.filter((p) => isSurvivor(p) && inLevel(p) && (graduate || p.wbProgramId !== null) && !isOffered(p)),
    [allPrograms, inLevel, graduate],
  )
  const graduateCount = useMemo(() => allPrograms.filter((p) => isSurvivor(p) && isGraduate(p)).length, [allPrograms])
  const otherRows = useMemo(() => other.filter(matchesSearch), [other, matchesSearch])
  const quickAdds = useMemo(
    () => unusedSuggestions(suggestedPrograms, wbPrograms, allPrograms),
    [suggestedPrograms, wbPrograms, allPrograms],
  )
  const slateRows = useMemo(
    () => slatePrograms.filter((s) => !needle || slateSearchText(s).toLowerCase().includes(needle)),
    [slatePrograms, needle],
  )

  const graduateColumns = buildGraduateColumns({ onFlag: collection.setFlagFor })
  const unassignedColumns = buildUnassignedColumns({
    wbOptions,
    onAssign: (p, target) => assignTo([p.id], target, programLabel(p)),
  })
  const slateColumns = buildSlateColumns({
    wbOptions,
    contentOf: (id) => slateContent.get(id),
    onMap: (slate, wbProgramId) =>
      mapSlate.mutate(
        { slateProgramId: slate.id, wbProgramId },
        {
          onSuccess: () => toast.push(wbProgramId ? `Mapped ${slate.slateName}.` : `Unmapped ${slate.slateName}.`),
          onError: () => toast.push('Could not change the WB program.', 'error'),
        },
      ),
  })

  const segmentName = (id: string) => segments.find((s) => s.id === id)?.name ?? 'Unassigned'

  /** Selection clears on success, as the other bulk actions do (PC-23). */
  function bulkSetSegment(segmentId: string | null) {
    const label = countLabel(selectedGroups.size, 'program')
    setWbSegment([...selectedGroups], segmentId, () => {
      toast.push(segmentId ? `Moved ${label} to ${segmentName(segmentId)}.` : `Unassigned ${label}.`)
      clearAll()
    })
  }

  /**
   * The whole client, not the current filter: the table is the client's
   * dropdown list and its Slate list, so a filtered copy would be a wrong one.
   * Built client-side from the WB programs and their slate mapping (ADR-0019 § 6).
   */
  const forward = useMemo(() => forwardRows(wbPrograms), [wbPrograms])
  const reverse = useMemo(() => reverseRows(slatePrograms), [slatePrograms])
  const translationsLoading = programsLoading || slateLoading || wbLoading

  function runExport() {
    downloadBlob(translationsCsv(forward, reverse, segments), `translations-${clientId}.csv`, 'text/csv;charset=utf-8')
    toast.push('Exported the translations table.')
  }

  const reviewFromDrawer = useCallback(
    (id: string, status: 'approved' | 'rejected') => {
      collection.setStatusFor(id, status)
      setEditing(null)
    },
    [collection],
  )

  function addProgram(name?: string) {
    setQuickAddName(name)
    setEditingWb('new')
  }

  function changeLevel(next: ProgramLevel) {
    setLevel(next)
    clearAll()
    // The two tabs hold different levels and degrees.
    setLevelFilter('all')
    setDegreeFilter('all')
    if (!LEVEL_VIEWS[next].includes(view)) setView('programs')
  }

  const viewTabs = (
    <div className="flex flex-col items-start gap-2 pb-2 pt-1">
      <LevelTabs
        value={level}
        counts={{ undergraduate: wbPrograms.length, graduate: graduateCount }}
        onChange={changeLevel}
      />
      <ViewTabs
        value={view}
        views={LEVEL_VIEWS[level]}
        counts={{
          programs: programCount,
          unassigned: unassigned.length,
          merged: merged.length,
          slate: slatePrograms.length,
          other: other.length,
        }}
        onChange={setView}
      />
    </div>
  )

  return (
    <>
      <ReviewSurface
        title="Programs and segments"
        description="Only approved programs are retrievable by the agent. Segments group related programs by subject, for filtering and export."
        headerActions={
          view === 'programs' && (
            <>
              {/* Translations and WB programs are undergraduate (PC-25). */}
              {!graduate && (
                <>
                  <Button onClick={() => setViewingTranslations(true)} disabled={translationsLoading}>
                    <Table2 size={14} aria-hidden />
                    View translations table
                  </Button>
                  <Button onClick={runExport} disabled={translationsLoading}>
                    <Download size={14} aria-hidden />
                    Export translations CSV
                  </Button>
                  <Button onClick={() => addProgram()}>
                    <Plus size={14} aria-hidden />
                    Add program
                  </Button>
                </>
              )}
              <Button variant="primary" onClick={queue.start} disabled={collection.counts.pending === 0}>
                <ClipboardCheck size={14} aria-hidden />
                Review pending
              </Button>
            </>
          )
        }
        filters={
          <>
            <div className="relative">
              <Search
                size={13}
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint"
              />
              <Input
                value={collection.query}
                onChange={(e) => collection.setQuery(e.target.value)}
                placeholder={view === 'slate' ? 'Search slate programs' : 'Search programs'}
                aria-label={view === 'slate' ? 'Search slate programs' : 'Search programs'}
                className="h-8 w-56 pl-7 text-[13px]"
              />
            </div>
            {view === 'programs' && (
              <>
                <Select
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                  aria-label="Filter by level"
                  className="h-8 w-40 text-[13px]"
                >
                  <option value="all">All levels</option>
                  {levelOptions.map((l) => (
                    <option key={l} value={l}>
                      {l === NOT_STATED ? 'No level' : levelLabel(l)}
                    </option>
                  ))}
                </Select>
                <Select
                  value={degreeFilter}
                  onChange={(e) => setDegreeFilter(e.target.value)}
                  aria-label="Filter by degree"
                  className="h-8 w-40 text-[13px]"
                >
                  <option value="all">All degrees</option>
                  {degreeOptions.map((d) => (
                    <option key={d} value={d}>
                      {d === NOT_STATED ? 'No degree' : d}
                    </option>
                  ))}
                </Select>
              </>
            )}
            {view === 'programs' && !graduate && (
              <span className="text-[12px] text-ink-faint">
                {segmentFilter === 'all'
                  ? 'All segments'
                  : segmentFilter === UNASSIGNED
                    ? 'Unassigned only'
                    : segmentName(segmentFilter)}
              </span>
            )}
          </>
        }
        tabs={
          <>
            {viewTabs}
            {view === 'programs' && (
              <StatusTabs value={collection.status} counts={counts} onChange={collection.setStatus} mark="flagged" />
            )}
          </>
        }
        aside={
          view === 'programs' &&
          !graduate && (
            <div className="flex flex-col gap-3">
              <SegmentRail
                segments={segments}
                defaults={defaultSegments}
                programs={wbPrograms}
                value={segmentFilter}
                onChange={setSegmentFilter}
                onCreate={(name) => segmentOps.create.mutate(name)}
                onRename={(id, name) => segmentOps.rename.mutate({ id, name })}
                onMove={(id, direction) => segmentOps.move.mutate({ id, direction })}
                onEditDetails={(segment) => setDetailsSegmentId(segment.id)}
                onRequestDelete={setDeletingSegment}
              />
              <ProgramQuickAdd suggestions={quickAdds} onPick={addProgram} />
            </div>
          )
        }
        bulkBar={
          view === 'programs' && (
            <BulkActionBar
              count={graduate ? collection.selected.size : selectedGroups.size}
              noun="program"
              tagPool={collection.tagPool}
              onClear={clearAll}
              onApprove={collection.bulk.approve}
              onReject={collection.bulk.reject}
              onAddTag={collection.bulk.addTag}
              onDelete={collection.bulk.requestDelete}
              segments={segments}
              onSetSegment={graduate ? undefined : bulkSetSegment}
            />
          )
        }
      >
        {view === 'programs' && graduate && (
          <DataTable
            rows={collection.rows}
            columns={graduateColumns}
            rowKey={(p) => p.id}
            loading={collection.isLoading}
            selected={collection.selected}
            onSelectedChange={collection.setSelected}
            onRowClick={(p) => setEditing(p)}
            rowClassName={(p) => (p.previouslyRejected ? 'bg-warn-50/40' : undefined)}
            empty={
              <EmptyState
                title={
                  collection.query || attributeFiltered
                    ? 'No graduate programs match these filters'
                    : `No ${collection.status} graduate programs`
                }
                description="Crawled programs at the graduate level. They have no WB program and are in no output yet. Change a program's level in its drawer to move it."
              />
            }
          />
        )}

        {view === 'programs' && !graduate && (
          <DataTable
            rows={groups}
            columns={columns}
            rowKey={(g) => g.id}
            loading={collection.isLoading || wbLoading}
            selected={selectedGroups}
            onSelectedChange={selectGroups}
            onRowClick={(g) => setEditingWb(g.wb)}
            rowClassName={(g) => (g.members.some((m) => m.previouslyRejected) ? 'bg-warn-50/40' : undefined)}
            empty={
              <EmptyState
                title={
                  collection.query || segmentFilter !== 'all' || attributeFiltered
                    ? 'No programs match these filters'
                    : `No ${collection.status} programs`
                }
                description={
                  collection.all.length === 0 && wbPrograms.length === 0
                    ? 'The last crawl did not produce any programs. Check the crawl scope, or add one manually.'
                    : undefined
                }
                action={
                  <Button size="sm" onClick={() => addProgram()}>
                    Add program
                  </Button>
                }
              />
            }
          />
        )}

        {view === 'unassigned' && (
          <DataTable
            rows={unassignedRows}
            columns={unassignedColumns}
            rowKey={(p) => p.id}
            loading={programsLoading}
            onRowClick={(p) => setEditing(p)}
            empty={
              <EmptyState
                title={needle ? 'No unassigned programs match this search' : 'No unassigned programs'}
                description="Crawled programs under no WB program: ones taken out of a WB program, or whose WB program was deleted."
              />
            }
          />
        )}

        {view === 'merged' && (
          <DataTable
            rows={mergedRows}
            columns={mergedColumns}
            rowKey={(p) => p.id}
            loading={programsLoading}
            onRowClick={(p) => setEditing(p)}
            empty={
              <EmptyState
                title={needle ? 'No merged programs match this search' : 'No merged programs'}
                description="Rows the pipeline consolidated into another program, such as a listing page naming a program without its degree."
              />
            }
          />
        )}

        {view === 'other' && (
          <DataTable
            rows={otherRows}
            columns={otherColumns}
            rowKey={(p) => p.id}
            loading={programsLoading}
            onRowClick={(p) => setEditing(p)}
            empty={
              <EmptyState
                title={needle ? 'No other programs match this search' : 'No other programs'}
                description="Programs the crawl found with no program page of their own, such as a name on a listing page."
              />
            }
          />
        )}

        {view === 'slate' && (
          <DataTable
            rows={slateRows}
            columns={slateColumns}
            rowKey={(s) => s.id}
            loading={slateLoading}
            onRowClick={(s) => setSlateContentId(s.id)}
            empty={
              <EmptyState
                title={needle ? 'No slate programs match this search' : 'No slate programs'}
                description={
                  needle ? undefined : 'This client has no slate list. Its programs are used without one.'
                }
              />
            }
          />
        )}
      </ReviewSurface>

      <WbProgramDrawer
        clientId={clientId!}
        wbProgram={openWb}
        initialName={editingWb === 'new' ? quickAddName : undefined}
        segments={segments}
        slatePrograms={slatePrograms}
        programs={allPrograms}
        onClose={() => {
          setEditingWb(null)
          setQuickAddName(undefined)
        }}
        onOpenProgram={setEditing}
        onAddDegree={(wb) => {
          setAddTo(wb)
          setEditing('new')
        }}
      />

      <SlateContentDrawer
        clientId={clientId!}
        slate={slatePrograms.find((s) => s.id === slateContentId) ?? null}
        onClose={() => setSlateContentId(null)}
        onOpenWbProgram={(id) => {
          const wb = wbById.get(id)
          if (!wb) return
          setSlateContentId(null)
          setEditingWb(wb)
        }}
      />

      <ProgramDrawer
        clientId={clientId!}
        program={editing}
        addTo={addTo}
        wbOptions={wbOptions}
        tagPool={collection.tagPool}
        onClose={() => {
          setEditing(null)
          setAddTo(null)
        }}
        onSave={async (patch) => {
          try {
            await upsert.mutateAsync(patch)
          } catch (e) {
            // The drawer stays open with the draft, so nothing typed is lost.
            const duplicate = e instanceof ApiError && e.status === 409
            toast.push(duplicate ? 'A program with this name and degree is already in the list.' : 'Could not save the program.', 'error')
            return
          }
          setEditing(null)
          setAddTo(null)
          toast.push(patch.id ? 'Program saved.' : 'Degree added.')
        }}
        onAssign={(programId, target) => {
          const program = allPrograms.find((p) => p.id === programId)
          assignTo([programId], target, program ? programLabel(program) : 'the program')
        }}
        onReview={reviewFromDrawer}
        onFlag={collection.setFlagFor}
      />

      <ProgramReviewQueue
        clientId={clientId!}
        queue={queue}
        wbOptions={wbOptions}
        tagPool={collection.tagPool}
        onFlag={collection.setFlagFor}
      />

      <SegmentDetailsModal
        clientId={clientId!}
        segment={segments.find((s) => s.id === detailsSegmentId) ?? null}
        onClose={() => setDetailsSegmentId(null)}
      />

      <DeleteSegmentModal
        segment={deletingSegment}
        segments={segments}
        affected={wbPrograms.filter((w) => w.segmentId === deletingSegment?.id).length}
        busy={segmentOps.remove.isPending}
        onClose={() => setDeletingSegment(null)}
        onConfirm={async (reassignTo) => {
          if (!deletingSegment) return
          await segmentOps.remove.mutateAsync({ id: deletingSegment.id, reassignTo })
          if (segmentFilter === deletingSegment.id) setSegmentFilter('all')
          setDeletingSegment(null)
          toast.push('Segment deleted.')
        }}
      />

      <TranslationsModal
        open={viewingTranslations}
        forward={forward}
        reverse={reverse}
        onClose={() => setViewingTranslations(false)}
        onExport={runExport}
      />

      {collection.deleteDialog}
    </>
  )
}
